import React from 'react';
import { WxbButton } from '../Button/Button';
import { WxbIcon } from '../Icon/Icon';
import type { GanttV5ContextMenuItem, GanttV5Task } from './types';

interface GanttV5ContextMenuProps {
  visible: boolean;
  x: number;
  y: number;
  task: GanttV5Task | null;
  items: GanttV5ContextMenuItem[];
  selectedCount: number;
  contextType: 'task' | 'group' | 'background';
  onAction: (key: string) => void;
  onClose: () => void;
}

const itemLabel = (item: GanttV5ContextMenuItem, selectedCount: number) => (
  selectedCount > 1 && item.batchLabel
    ? item.batchLabel.replace('{n}', String(selectedCount))
    : item.label
);

export const GanttV5ContextMenu: React.FC<GanttV5ContextMenuProps> = ({
  visible,
  x,
  y,
  task,
  items,
  selectedCount,
  contextType,
  onAction,
  onClose,
}) => {
  const menuRef = React.useRef<HTMLDivElement>(null);
  const [expandedItemKey, setExpandedItemKey] = React.useState<string | null>(null);

  React.useLayoutEffect(() => {
    const element = menuRef.current;
    if (!visible || !element) return;
    const bounds = element.getBoundingClientRect();
    const left = Math.max(8, Math.min(x, window.innerWidth - bounds.width - 8));
    const top = Math.max(8, Math.min(y, window.innerHeight - bounds.height - 8));
    element.style.setProperty('--wxb-gantt-v5-menu-x', `${left}px`);
    element.style.setProperty('--wxb-gantt-v5-menu-y', `${top}px`);
  }, [visible, x, y, items.length]);

  React.useEffect(() => {
    if (!visible) return undefined;
    setExpandedItemKey(null);
    const closeOnPointer = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) onClose();
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', closeOnPointer);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnPointer);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [onClose, visible]);

  if (!visible) return null;

  const heading = selectedCount > 1
    ? `已选择 ${selectedCount} 个任务`
    : task?.label ?? (contextType === 'group' ? '分组操作' : '甘特操作');

  const runAction = (key: string, disabled?: boolean) => {
    if (disabled) return;
    onAction(key);
    onClose();
  };

  return (
    <div ref={menuRef} className="wxb-gantt-v5-context-menu" role="menu" aria-label={heading}>
      <div className="wxb-gantt-v5-context-menu__header">{heading}</div>
      {items.map((item) => (
        <React.Fragment key={item.key}>
          <div
            className="wxb-gantt-v5-context-menu__item-wrap"
            onMouseEnter={() => item.children?.length && setExpandedItemKey(item.key)}
            onMouseLeave={() => item.children?.length && setExpandedItemKey(null)}
          >
            <WxbButton
              variant={item.danger ? 'danger' : 'ghost'}
              size="sm"
              className="wxb-gantt-v5-context-menu__item"
              role="menuitem"
              disabled={item.disabled}
              onClick={() => item.children?.length ? setExpandedItemKey(item.key) : runAction(item.key, item.disabled)}
            >
              {item.icon && <span className="wxb-gantt-v5-context-menu__icon">{item.icon}</span>}
              <span className="wxb-gantt-v5-context-menu__label">{itemLabel(item, selectedCount)}</span>
              {item.shortcut && <span className="wxb-gantt-v5-context-menu__shortcut">{item.shortcut}</span>}
              {item.children?.length && <WxbIcon name="chevron-right" size={12} />}
            </WxbButton>
            {item.children?.length && expandedItemKey === item.key && (
              <div className="wxb-gantt-v5-context-menu__submenu" role="menu">
                {item.children.map((child) => (
                  <WxbButton
                    key={child.key}
                    variant={child.danger ? 'danger' : 'ghost'}
                    size="sm"
                    className="wxb-gantt-v5-context-menu__item"
                    role="menuitem"
                    disabled={child.disabled}
                    onClick={() => runAction(child.key, child.disabled)}
                  >
                    {child.icon && <span className="wxb-gantt-v5-context-menu__icon">{child.icon}</span>}
                    <span className="wxb-gantt-v5-context-menu__label">{child.label}</span>
                    {child.shortcut && <span className="wxb-gantt-v5-context-menu__shortcut">{child.shortcut}</span>}
                  </WxbButton>
                ))}
              </div>
            )}
          </div>
          {item.divider && <div className="wxb-gantt-v5-context-menu__divider" role="separator" />}
        </React.Fragment>
      ))}
    </div>
  );
};

export default GanttV5ContextMenu;
